import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Collapse,
  Avatar,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  AccountBalance as FinanceIcon,
  Campaign as CampaignIcon,
  Apps as AppsIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  ShoppingCart as ShoppingCartIcon,
  HealthAndSafety as InsuranceIcon,
  ExpandLess,
  ExpandMore,
  Notifications as NotificationsIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
} from '@mui/icons-material';
import authService from '../../services/auth.service';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { notificationService } from '../../services/notification.service';
import { filterMenusByRole, MenuItem as FilterMenuItem } from '../../utils/menuFilter';
import {
  requestWebNotificationPermission,
  showWebNotification,
} from '../../utils/webNotifications';
import { NOTIFICATIONS_UPDATED_EVENT } from '../../utils/notificationEvents';
import NotificationToastStack, {
  ToastNotification,
} from '../notifications/NotificationToastStack';

const drawerWidth = 240;

const SIDEBAR_BG = '#1a2332';
const SIDEBAR_ACTIVE = '#2563eb';
const SIDEBAR_HOVER = 'rgba(255,255,255,0.08)';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});
  const [userRole, setUserRole] = useState<any>(null);
  const [userDisplayName, setUserDisplayName] = useState('');
  const [notificationCount, setNotificationCount] = useState(0);
  const [toastNotifications, setToastNotifications] = useState<ToastNotification[]>([]);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    // Get user's role for menu filtering
    const fetchUserRole = async () => {
      try {
        const user = authService.getCurrentUser();
        if (user) {
          const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ')
            || user.username
            || user.email
            || '';
          setUserDisplayName(displayName);
          // Always fetch fresh user data to ensure role is populated
          try {
            const response = await authService.getMe();
            const userData = response;
            if (userData.role) {
              setUserRole(userData.role);
              // Update localStorage with fresh user data including role
              const updatedUser = {
                ...user,
                role: userData.role,
              };
              localStorage.setItem('user', JSON.stringify(updatedUser));
            } else if (user.role) {
              setUserRole(user.role);
            } else {
              setUserRole(null);
            }
          } catch (error) {
            console.error('Error fetching user role:', error);
            // Fallback to stored user role
            if (user.role) {
              setUserRole(user.role);
            } else {
              setUserRole(null);
            }
          }
        } else {
          setUserRole(null);
        }
      } catch (error) {
        console.error('Error getting user role:', error);
        setUserRole(null);
      }
    };

    fetchUserRole();
  }, []);

  useEffect(() => {
    requestWebNotificationPermission().catch(() => {
      // Permission prompt may fail outside a secure context
    });
  }, []);

  useEffect(() => {
    let prevCount = 0;
    let initialLoadDone = false;
    let lastNotifiedId: number | null = null;
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let unsubscribe: (() => void) | null = null;

    const playNotificationSound = () => {
      try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(880, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.3);
        gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.5);
      } catch (e) {
        // Audio API not available — silent fallback
      }
    };

    const pushToast = (n: {
      id?: number;
      title: string;
      message: string;
      link?: string;
      type?: ToastNotification['type'];
    }) => {
      if (!n.title) return;
      const toastKey = Date.now();
      setToastNotifications((prev) => {
        const next: ToastNotification[] = [
          {
            id: n.id ?? toastKey,
            title: n.title,
            message: n.message || '',
            link: n.link,
            type: n.type,
            toastKey,
          },
          ...prev.filter((t) => t.id !== n.id),
        ];
        return next.slice(0, 5);
      });
    };

    const applyUpdate = (payload: {
      count: number;
      notification?: {
        id?: number;
        type?: ToastNotification['type'];
        title?: string;
        message?: string;
        link?: string;
      } | null;
    }) => {
      const newCount = payload.count || 0;
      const isNewAlert = initialLoadDone && newCount > prevCount;

      if (isNewAlert) {
        playNotificationSound();
        const n = payload.notification;
        if (n?.title && n.id !== lastNotifiedId) {
          lastNotifiedId = n.id ?? null;
          pushToast({
            id: n.id,
            title: n.title,
            message: n.message || '',
            link: n.link,
            type: n.type,
          });
          showWebNotification({
            id: n.id,
            title: n.title,
            message: n.message || '',
            link: n.link,
          });
        }
      }

      prevCount = newCount;
      setNotificationCount(newCount);
      initialLoadDone = true;
    };

    const fetchNotificationCount = async () => {
      try {
        const response = await notificationService.getUnreadCount();
        applyUpdate({ count: response.data?.count || 0 });
      } catch (error) {
        console.error('Error fetching notification count:', error);
      }
    };

    const startPolling = (ms: number) => {
      if (pollInterval) clearInterval(pollInterval);
      pollInterval = setInterval(fetchNotificationCount, ms);
    };

    const scheduleReconnect = () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connectStream();
      }, 10000);
    };

    const connectStream = () => {
      unsubscribe?.();
      unsubscribe = notificationService.subscribeUnreadCount(applyUpdate, () => {
        // SSE disconnected — fall back to polling and retry later
        fetchNotificationCount();
        startPolling(document.hidden ? 30000 : 5000);
        scheduleReconnect();
      });
    };

    fetchNotificationCount();
    connectStream();
    startPolling(document.hidden ? 30000 : 8000);

    const onVisibility = () => {
      if (document.hidden) {
        startPolling(30000);
      } else {
        fetchNotificationCount();
        startPolling(8000);
        connectStream();
      }
    };

    const onFocus = () => fetchNotificationCount();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, fetchNotificationCount);

    return () => {
      unsubscribe?.();
      if (pollInterval) clearInterval(pollInterval);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, fetchNotificationCount);
    };
  }, []);

  const handleToastDismiss = (toastKey: number) => {
    setToastNotifications((prev) => prev.filter((t) => t.toastKey !== toastKey));
  };

  const handleToastClick = (item: ToastNotification) => {
    handleToastDismiss(item.toastKey);
    if (item.link) {
      const path = item.link.startsWith('/') ? item.link : `/${item.link}`;
      navigate(path);
    } else {
      navigate('/notifications');
    }
  };

  const toastAnchor = i18n.language?.startsWith('ar') ? 'left' : 'right';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleMenuClick = (menuId: string) => {
    setOpenMenus((prev) => {
      const isCurrentlyOpen = prev[menuId];
      // If opening this menu, close all others (accordion behavior)
      if (!isCurrentlyOpen) {
        return { [menuId]: true };
      }
      // If closing, just close this one
      return { [menuId]: false };
    });
  };

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', JSON.stringify(newMode));
    window.dispatchEvent(new Event('darkModeChanged'));
    document.body.style.backgroundColor = newMode ? '#121212' : '#ffffff';
    document.body.style.color = newMode ? '#ffffff' : '#000000';
  };

  const allMenuItems = [
    // Dashboard Section
    {
      id: 'dashboard',
      textKey: 'navigation.dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
    },
    {
      id: 'reports',
      textKey: 'navigation.reports',
      icon: <AppsIcon />,
      children: [
        { textKey: 'navigation.reportsPage', path: '/reports' },
        { textKey: 'navigation.serviceProviderReport', path: '/service-providers/report' },
        { textKey: 'navigation.users', path: '/users' },
        { textKey: 'navigation.rolesPermissions', path: '/roles' },
        { textKey: 'navigation.adminApprovals', path: '/admin-approvals' },
        { textKey: 'navigation.approvalSettings', path: '/settings/approvals' },
        { textKey: 'navigation.servicePackages', path: '/service-packages' },
      ],
    },
    
    // Operations Department
    {
      id: 'operations',
      textKey: 'navigation.operations',
      icon: <WorkIcon />,
      children: [
        { textKey: 'navigation.overview', path: '/operations/overview' },
        { textKey: 'navigation.serviceManagement', path: '/service-providers' },
        { textKey: 'navigation.serviceSchedule', path: '/service-providers/schedule' },
        { textKey: 'navigation.requests', path: '/requests' },
        { textKey: 'navigation.requestItemsReport', path: '/requests/report' },
        { textKey: 'navigation.insuranceCompanies', path: '/insurance-companies' },
        { textKey: 'navigation.insuranceClients', path: '/insurance-clients' },
        { textKey: 'navigation.servicePackages', path: '/service-packages' },
        { textKey: 'navigation.whatsappChat', path: '/operations/whatsapp-chat' },
        { textKey: 'navigation.whatsappTracker', path: '/operations/whatsapp-tracker' },
        { textKey: 'navigation.automationDashboard', path: '/operations/automation' },
      ],
    },
    
    // Organization Department
    {
      id: 'organization',
      textKey: 'navigation.organization',
      icon: <PeopleIcon />,
      children: [
        { textKey: 'navigation.overview', path: '/organization/overview' },
        { textKey: 'navigation.employees', path: '/employees' },
        { textKey: 'navigation.departments', path: '/departments' },
        { textKey: 'navigation.attendance', path: '/attendance' },
        { textKey: 'navigation.leaveRequests', path: '/leave-requests' },
        { textKey: 'navigation.payroll', path: '/payroll' },
      ],
    },
    
    // Sales & CRM Department
    {
      id: 'sales-crm',
      textKey: 'navigation.salesCRM',
      icon: <ShoppingCartIcon />,
      children: [
        { textKey: 'navigation.overview', path: '/sales/overview' },
        { textKey: 'navigation.salesOrders', path: '/sales-orders' },
        { textKey: 'navigation.contacts', path: '/contacts' },
        { textKey: 'navigation.contactGroups', path: '/contact-groups' },
      ],
    },
    
    // Marketing Department
    {
      id: 'marketing',
      textKey: 'navigation.marketing',
      icon: <CampaignIcon />,
      children: [
        { textKey: 'navigation.overview', path: '/marketing/overview' },
        { textKey: 'navigation.whatsapp', path: '/operations/whatsapp-chat' },
        { textKey: 'navigation.instagram', path: '/instagram' },
        { textKey: 'navigation.websiteBlog', path: '/marketing/blog' },
        { textKey: 'navigation.linkedin', path: '/marketing/linkedin' },
        { textKey: 'navigation.twitter', path: '/marketing/twitter' },
        { textKey: 'navigation.campaigns', path: '/marketing/campaigns' },
      ],
    },
    
    // Finance Department
    {
      id: 'finance',
      textKey: 'navigation.finance',
      icon: <FinanceIcon />,
      children: [
        { textKey: 'navigation.overview', path: '/finance/overview' },
        { textKey: 'navigation.quotations', path: '/quotations' },
        { textKey: 'navigation.purchase', path: '/purchase-orders' },
        { textKey: 'navigation.suppliers', path: '/suppliers' },
        { textKey: 'navigation.invoices', path: '/invoices' },
        { textKey: 'navigation.payments', path: '/payments' },
        { textKey: 'navigation.chartOfAccounts', path: '/accounts' },
        { textKey: 'navigation.financialReports', path: '/financial-reports' },
        { textKey: 'navigation.taxReport', path: '/finance/tax' },
        { textKey: 'navigation.payroll', path: '/payroll' },
        { textKey: 'navigation.customers', path: '/customers' },
        { textKey: 'navigation.products', path: '/products' },
      ],
    },
    
    // Coming Soon - Insurance Broker
    {
      id: 'insurance-broker',
      textKey: 'navigation.insuranceBroker',
      icon: <InsuranceIcon />,
      path: '/insurance-broker',
    },
    
    // Settings
    {
      id: 'settings',
      textKey: 'navigation.settings',
      icon: <SettingsIcon />,
      children: [
        { textKey: 'navigation.systemSettings', path: '/settings' },
        { textKey: 'navigation.systemLogs', path: '/system-logs' },
      ],
    },
  ];

  // Filter menu based on user's role
  const menuStructure = filterMenusByRole(allMenuItems as FilterMenuItem[], userRole);

  const drawer = (
    <Box sx={{
      height: '100%',
      backgroundColor: SIDEBAR_BG,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Box sx={{
        p: 2,
        textAlign: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <Typography
          variant="h6"
          sx={{
            color: 'white',
            fontWeight: 700,
            fontSize: '13px',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
          }}
        >
          {t('navigation.menu')}
        </Typography>
      </Box>

      <List sx={{
        flex: 1,
        pt: 1,
        px: 1,
        backgroundColor: SIDEBAR_BG,
        '& .MuiCollapse-root': { backgroundColor: SIDEBAR_BG },
      }}>
        {menuStructure.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {t('navigation.loadingMenu')}
            </Typography>
          </Box>
        )}
        {menuStructure.map((item) => {
          const isActive = location.pathname === item.path;
          const hasChildren = item.children && item.children.length > 0;
          const isParentActive = hasChildren && item.children?.some(child => location.pathname === child.path);
          
          return (
            <React.Fragment key={item.id}>
              <ListItem
                onClick={() => {
                  if (hasChildren) {
                    handleMenuClick(item.id);
                  } else if (item.path) {
                    navigate(item.path);
                    if (isMobile) handleDrawerToggle();
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: (isActive || isParentActive) ? SIDEBAR_ACTIVE : 'transparent',
                  borderLeft: '3px solid transparent',
                  mb: 0.5,
                  borderRadius: '8px',
                  px: 1.5,
                  py: 1,
                  '&:hover': {
                    backgroundColor: (isActive || isParentActive) ? SIDEBAR_ACTIVE : SIDEBAR_HOVER,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: '40px' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={t(item.textKey)} 
                  sx={{ 
                    color: 'white',
                    '& .MuiListItemText-primary': {
                      fontWeight: (isActive || isParentActive) ? 'bold' : 'normal',
                      fontSize: '14px'
                    }
                  }}
                />
                {hasChildren && (
                  openMenus[item.id] ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />
                )}
              </ListItem>
              
              {hasChildren && (
                <Collapse
                  in={openMenus[item.id]}
                  timeout="auto"
                  unmountOnExit
                  sx={{ backgroundColor: SIDEBAR_BG }}
                >
                  <List
                    component="div"
                    disablePadding
                    sx={{ backgroundColor: SIDEBAR_BG }}
                  >
                    {item.children?.map((child) => {
                      const isChildActive = location.pathname === child.path;
                      return (
                        <ListItem
                          key={child.path || 'no-path'}
                          onClick={() => {
                            if (child.path) {
                              navigate(child.path);
                              if (isMobile) handleDrawerToggle();
                            }
                          }}
                          sx={{
                            pl: 4,
                            cursor: 'pointer',
                            backgroundColor: isChildActive ? SIDEBAR_ACTIVE : 'transparent',
                            mb: 0.5,
                            borderRadius: '8px',
                            px: 1.5,
                            py: 0.75,
                            '&:hover': {
                              backgroundColor: isChildActive ? SIDEBAR_ACTIVE : SIDEBAR_HOVER,
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <ListItemText 
                            primary={t(child.textKey)} 
                            sx={{ 
                              color: 'white',
                              '& .MuiListItemText-primary': {
                                fontWeight: isChildActive ? 'bold' : 'normal',
                                fontSize: '13px'
                              }
                            }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          background: 'linear-gradient(90deg, #1e293b 0%, #334155 50%, #475569 100%)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          height: '64px',
          zIndex: 1300,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: 3, minHeight: '64px !important' }}>
          {/* Left side - Logo and User Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            
            {/* Logo Image */}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
              <img
                src="/cropped-pixelcut-export-removebg-2048x691.png"
                alt="Darb National Services Co LLC"
                style={{
                  height: '48px',
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{
                width: 32,
                height: 32,
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: 'white',
              }}>
                <AccountCircleIcon fontSize="small" />
              </Avatar>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="caption" sx={{ color: 'white', fontSize: '12px', lineHeight: 1.2, fontWeight: 600 }}>
                  {userDisplayName || t('user.profile')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '10px', lineHeight: 1.2 }}>
                  {userRole?.name || t('navigation.dashboard')}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right side - Icons and search */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Notifications */}
            <IconButton 
              color="inherit" 
              onClick={() => navigate('/notifications')}
              sx={{ color: 'white', p: 1 }}
            >
              <Badge badgeContent={notificationCount} color="error">
                <NotificationsIcon fontSize="small" />
              </Badge>
            </IconButton>

            {/* Dark Mode Toggle */}
            <IconButton 
              color="inherit" 
              onClick={toggleDarkMode}
              sx={{ color: 'white', p: 1 }}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>

            {/* Settings Menu */}
            <IconButton 
              color="inherit" 
              onClick={handleProfileMenuOpen}
              sx={{ color: 'white', p: 1 }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>

            {/* Profile Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem
                onClick={() => {
                  navigate('/profile');
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <AccountCircleIcon fontSize="small" />
                </ListItemIcon>
                {t('user.profile')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  navigate('/settings');
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                {t('navigation.settings')}
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                {t('auth.logout')}
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar positioned under the top menu */}
      <Box
        component="nav"
        sx={{ 
          width: { sm: drawerWidth }, 
          flexShrink: { sm: 0 },
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              top: '64px', // Position under the top menu
              height: 'calc(100vh - 64px)'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              top: '64px',
              height: 'calc(100vh - 64px)',
              borderRight: 'none',
            },
          }}
          open
          hideBackdrop
          ModalProps={{
            keepMounted: false,
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: '#f0f2f5',
          width: '100%',
        }}
      >
        {children}
      </Box>

      <NotificationToastStack
        items={toastNotifications}
        onDismiss={handleToastDismiss}
        onClick={handleToastClick}
        anchor={toastAnchor}
      />
    </Box>
  );
};

export default MainLayout;
